import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Check } from 'lucide-react';
import { Button } from '@/components/ui/button';

const PLANS = [
  {
    name: 'Launch',
    price: 'Free',
    priceSuffix: '',
    description: 'For early-stage founders getting started',
    features: [
      '1 company',
      'Basic cap table',
      'Initial valuation estimate',
      'Up to 10 shareholders',
      'Share register',
    ],
    cta: 'Start Free',
    variant: 'outline' as const,
    highlight: false,
  },
  {
    name: 'Pro',
    price: '$99',
    priceSuffix: '/mo',
    description: 'For founders raising Seed to Series A',
    features: [
      'Unlimited funding rounds',
      'Share register & certificates',
      '3 valuation runs per month',
      'Closing packs',
      'Investor reporting',
      'Scenario modeling',
      'Data room',
    ],
    cta: 'Upgrade to Pro',
    variant: 'default' as const,
    highlight: true,
  },
  {
    name: 'Growth',
    price: '$299',
    priceSuffix: '/mo',
    description: 'For scaling companies and complex structures',
    features: [
      'Unlimited valuations',
      'Multi-jurisdiction compliance',
      'AI valuation advisor',
      'Audit & regulator access',
      'Advanced reporting',
      'Board portal',
      'Priority support',
    ],
    cta: 'Upgrade to Growth',
    variant: 'outline' as const,
    highlight: false,
  },
];

const fadeUp = {
  hidden: { opacity: 0, y: 20 },
  visible: (i: number) => ({
    opacity: 1,
    y: 0,
    transition: { delay: i * 0.1, duration: 0.4 },
  }),
};

export default function Pricing() {
  const navigate = useNavigate();

  return (
    <section className="min-h-screen bg-background px-6 py-16">
      <div className="max-w-5xl mx-auto">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
          className="text-center mb-12"
        >
          <h1 className="text-3xl md:text-4xl font-semibold text-foreground">
            Simple, transparent pricing
          </h1>
          <p className="mt-3 text-muted-foreground">Start free. Upgrade when you raise or scale.</p>
        </motion.div>

        {/* Pricing Grid */}
        <div className="grid md:grid-cols-3 gap-6">
          {PLANS.map((plan, i) => (
            <motion.div
              key={plan.name}
              custom={i}
              initial="hidden"
              animate="visible"
              variants={fadeUp}
              className={`rounded-xl p-6 flex flex-col ${
                plan.highlight
                  ? 'border-2 border-primary bg-card shadow-md'
                  : 'border border-border bg-card'
              }`}
            >
              {plan.highlight && (
                <span className="text-[10px] font-medium uppercase tracking-widest text-primary mb-3">
                  Most popular
                </span>
              )}
              <h2
                className={`text-lg font-medium ${plan.highlight ? 'text-primary' : 'text-foreground'}`}
              >
                {plan.name}
              </h2>
              <p className="text-sm text-muted-foreground mt-1">{plan.description}</p>

              <div className="mt-6">
                <span className="text-3xl font-semibold tabular-nums">{plan.price}</span>
                {plan.priceSuffix && (
                  <span className="text-sm text-muted-foreground">{plan.priceSuffix}</span>
                )}
              </div>

              <ul className="mt-6 space-y-2.5 flex-1">
                {plan.features.map((f) => (
                  <li key={f} className="flex items-start gap-2 text-sm text-muted-foreground">
                    <Check className="h-4 w-4 text-primary shrink-0 mt-0.5" />
                    {f}
                  </li>
                ))}
              </ul>

              <Button
                variant={plan.variant}
                className="mt-8 w-full"
                onClick={() => navigate('/auth')}
              >
                {plan.cta}
              </Button>
            </motion.div>
          ))}
        </div>

        {/* Value Statement */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.5, duration: 0.5 }}
          className="mt-16 text-center max-w-2xl mx-auto"
        >
          <h3 className="text-xl font-semibold text-foreground">Not just a cap table tool</h3>
          <p className="mt-3 text-muted-foreground text-sm leading-relaxed">
            CapLedger replaces spreadsheets, valuation guesswork, and compliance friction — giving
            you a single system to manage equity, raise capital, and stay in control.
          </p>
        </motion.div>

        {/* Footer CTA */}
        <div className="mt-12 text-center">
          <Button size="lg" onClick={() => navigate('/auth')}>
            Get Started
          </Button>
          <p className="text-xs text-muted-foreground mt-2">
            Free to start. No credit card required.
          </p>
        </div>
      </div>
    </section>
  );
}
